import type {
  Category,
  ContentPack,
  EquivalenceRule,
  ImportValidationResult,
  Language,
  ManualEquivalenceGroup,
  NormalizedAlias,
  NormalizedRiddle,
  RiddleStatus,
} from './types';
import { normalizeAnswer } from './text';

const VALID_STATUSES = new Set<RiddleStatus>([
  'draft',
  'needs_review',
  'approved',
  'declined',
  'published',
  'disabled',
  'archived',
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function readNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function identifierPart(value: string, fallback: string): string {
  const normalized = value
    .toLocaleLowerCase('fr-FR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || fallback;
}

function aliasesFrom(value: unknown): NormalizedAlias[] {
  if (!Array.isArray(value)) return [];
  const aliases = value
    .map((item) => {
      if (typeof item === 'string') return { display: item.trim(), type: 'accepted', is_full_answer: true };
      const record = asRecord(item);
      if (!record) return null;
      return {
        display: readString(record.display, record.value, record.name, record.answer),
        type: readString(record.type, record.alias_type, 'accepted'),
        is_full_answer: readBoolean(record.is_full_answer, true),
      };
    })
    .filter((item): item is NormalizedAlias => Boolean(item?.display));

  return Array.from(
    new Map(aliases.map((alias) => [normalizeAnswer(alias.display), alias])).values(),
  );
}

function getAnswer(raw: Record<string, unknown>): { display: string; aliases: NormalizedAlias[] } {
  const answerRecord = asRecord(raw.answer);
  const display = readString(
    raw.answer_display,
    typeof raw.answer === 'string' ? raw.answer : undefined,
    answerRecord?.display,
    answerRecord?.value,
    answerRecord?.name,
  );

  return {
    display,
    aliases: aliasesFrom(answerRecord?.aliases ?? raw.aliases),
  };
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string').map((item) => item.trim()).filter(Boolean) : [];
}

function manualGroups(value: unknown): ManualEquivalenceGroup[] {
  if (!Array.isArray(value)) return [];
  return value.map(asRecord).filter((item): item is Record<string, unknown> => Boolean(item)).map((item, index) => ({
    group_key: readString(item.group_key) || `manual_${index}`,
    variants: stringArray(item.variants),
    rule_type: readString(item.rule_type, 'equivalent'),
    sort_order: readNumber(item.sort_order, index),
  })).filter((item) => item.variants.length > 0);
}

function getContent(raw: Record<string, unknown>): string {
  const documentRecord = asRecord(raw.document);
  const clueRecord = asRecord(raw.clue);
  return readString(
    raw.content_text,
    raw.source_text,
    raw.content,
    raw.text,
    raw.description,
    raw.synopsis,
    documentRecord?.text,
    clueRecord?.text,
  );
}

function getGameData(raw: Record<string, unknown>): Record<string, unknown> {
  const explicit = asRecord(raw.game_data) ?? asRecord(raw.gameData);
  if (explicit) return explicit;

  const copied: Record<string, unknown> = {};
  const keys = [
    'tokens',
    'words',
    'visible_words',
    'default_visible_words',
    'reveal_rules',
    'punctuation',
    'hints',
    'is_daily_eligible',
    'manual_excluded_words',
    'manual_equivalence_groups',
  ];
  for (const key of keys) {
    if (key in raw) copied[key] = raw[key];
  }
  return copied;
}

export function extractRiddleArray(parsed: unknown): Record<string, unknown>[] {
  if (Array.isArray(parsed)) {
    return parsed.map(asRecord).filter((item): item is Record<string, unknown> => Boolean(item));
  }

  const root = asRecord(parsed);
  if (!root) throw new Error('Le fichier JSON doit contenir un tableau ou un objet.');

  for (const key of ['riddles', 'puzzles', 'items', 'data', 'enigmes', 'énigmes']) {
    const value = root[key];
    if (Array.isArray(value)) {
      const rootPackId = readString(root.content_pack_id, root.id);
      const rootLanguageCode = readString(root.language_code, root.language);

      return value
        .map(asRecord)
        .filter((item): item is Record<string, unknown> => Boolean(item))
        .map((item) => ({
          ...item,
          content_pack_id: readString(item.content_pack_id, item.pack_id, rootPackId),
          language_code: readString(item.language_code, item.language, rootLanguageCode),
        }));
    }
  }

  throw new Error(
    'Aucun tableau d’énigmes trouvé. Clés prises en charge : riddles, puzzles, items, data, enigmes.',
  );
}

export interface ImportPackMetadata {
  id: string;
  language_code: string;
  version: string | number | null;
  title: string;
  description: string | null;
}

export interface ParsedImportFile {
  riddles: Record<string, unknown>[];
  categories: Category[];
  equivalenceRules: EquivalenceRule[];
  pack: ImportPackMetadata | null;
}

function normalizeCategory(raw: Record<string, unknown>, index: number): Category {
  return {
    id: readString(raw.id),
    label: readString(raw.title, raw.label),
    description: readString(raw.description) || null,
    icon_key: readString(raw.icon_key) || null,
    sort_order: readNumber(raw.sort_order, index),
    is_active: readBoolean(raw.is_active, true),
  };
}

function normalizeEquivalenceRule(
  raw: Record<string, unknown>,
  index: number,
  packId: string,
  languageCode: string,
): EquivalenceRule {
  const contentPackId = readString(raw.content_pack_id, packId);
  const puzzleId = readString(raw.puzzle_id) || null;
  const scope = readString(raw.scope, 'global');
  const groupKey = readString(raw.group_key);
  const generatedId = [
    'eq',
    identifierPart(contentPackId, 'pack'),
    identifierPart(puzzleId ?? 'global', 'global'),
    identifierPart(scope, 'global'),
    identifierPart(groupKey, `group_${index}`),
  ].join('_');

  return {
    id: readString(raw.id, generatedId),
    content_pack_id: contentPackId,
    puzzle_id: puzzleId,
    language_code: readString(raw.language_code, languageCode),
    scope,
    group_key: groupKey,
    variants: Array.isArray(raw.variants)
      ? raw.variants
          .filter((variant): variant is string => typeof variant === 'string')
          .map((variant) => variant.trim())
          .filter(Boolean)
      : [],
    rule_type: readString(raw.rule_type),
    sort_order: readNumber(raw.sort_order, index),
    is_active: readBoolean(raw.is_active, true),
  };
}

export function parseImportFile(filename: string, content: string): ParsedImportFile {
  const extension = filename.split('.').pop()?.toLocaleLowerCase('fr-FR');
  if (extension === 'jsonl' || extension === 'ndjson') {
    const riddles = content
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line, index) => {
        try {
          const parsed = JSON.parse(line);
          const record = asRecord(parsed);
          if (!record) throw new Error('La ligne ne contient pas un objet JSON.');
          return record;
        } catch (error) {
          throw new Error(`JSONL invalide à la ligne ${index + 1}: ${error instanceof Error ? error.message : String(error)}`);
        }
      });
    return { riddles, categories: [], equivalenceRules: [], pack: null };
  }

  if (extension === 'csv') {
    throw new Error('L’import CSV n’est pas activé dans cette première version. Convertis le fichier en JSON ou JSONL.');
  }

  const parsed = JSON.parse(content) as unknown;
  const root = asRecord(parsed);
  const packId = readString(root?.id, root?.content_pack_id);
  const languageCode = readString(root?.language_code, root?.language);
  const categoryRows = Array.isArray(root?.categories) ? root.categories : [];
  const ruleRows = Array.isArray(root?.equivalence_rules) ? root.equivalence_rules : [];

  return {
    riddles: extractRiddleArray(parsed),
    categories: categoryRows
      .map(asRecord)
      .filter((item): item is Record<string, unknown> => Boolean(item))
      .map(normalizeCategory),
    equivalenceRules: ruleRows
      .map(asRecord)
      .filter((item): item is Record<string, unknown> => Boolean(item))
      .map((rule, index) => normalizeEquivalenceRule(rule, index, packId, languageCode)),
    pack: root
      ? {
          id: packId,
          language_code: languageCode,
          version:
            typeof root.version === 'string' || typeof root.version === 'number'
              ? root.version
              : null,
          title: readString(root.title, root.name),
          description: readString(root.description) || null,
        }
      : null,
  };
}

export function normalizeRiddle(raw: Record<string, unknown>): NormalizedRiddle {
  const answer = getAnswer(raw);
  const gameData = getGameData(raw);
  const statusCandidate = readString(raw.status) as RiddleStatus;
  const difficulty = Math.min(
    5,
    Math.max(1, Math.round(readNumber(raw.difficulty ?? raw.difficulty_estimated, 2))),
  );

  return {
    id: readString(raw.id, raw.riddle_id),
    content_pack_id: readString(raw.content_pack_id, raw.pack_id),
    category_id: readString(raw.category_id, raw.category),
    language_code: readString(raw.language_code, raw.language, 'fr'),
    title_internal: readString(raw.title_internal, raw.title, answer.display),
    content_text: getContent(raw),
    answer_display: answer.display,
    aliases: answer.aliases,
    is_daily_eligible: readBoolean(raw.is_daily_eligible ?? gameData.is_daily_eligible, true),
    manual_excluded_words: stringArray(raw.manual_excluded_words ?? gameData.manual_excluded_words),
    manual_equivalence_groups: manualGroups(raw.manual_equivalence_groups ?? gameData.manual_equivalence_groups),
    difficulty,
    status: VALID_STATUSES.has(statusCandidate) ? statusCandidate : 'draft',
    game_data: gameData,
    source_payload: raw,
    content_hash: typeof raw.content_hash === 'string' ? raw.content_hash : null,
  };
}

export interface ValidationReferences {
  languages: Language[];
  categories: Category[];
  packs: ContentPack[];
}

export function validateImport(
  rows: Record<string, unknown>[],
  references: ValidationReferences,
  importedCategories: Category[] = [],
): ImportValidationResult[] {
  const languageCodes = new Set(references.languages.map((item) => item.code));
  const categoryIds = new Set(
    [...references.categories, ...importedCategories].map((item) => item.id),
  );
  const packsById = new Map(references.packs.map((item) => [item.id, item]));
  const seenIds = new Set<string>();

  return rows.map((raw, index) => {
    const errors: string[] = [];
    const warnings: string[] = [];
    let normalized: NormalizedRiddle | null = null;

    try {
      normalized = normalizeRiddle(raw);
    } catch (error) {
      errors.push(error instanceof Error ? error.message : String(error));
    }

    if (normalized) {
      if (!normalized.id) errors.push('Identifiant manquant.');
      else if (!/^[a-z0-9_]+$/.test(normalized.id)) {
        errors.push('L’identifiant doit contenir uniquement des minuscules, chiffres et underscores.');
      } else if (seenIds.has(normalized.id)) {
        errors.push('Identifiant dupliqué dans le fichier.');
      }
      if (normalized.id) seenIds.add(normalized.id);

      if (!normalized.content_pack_id) errors.push('content_pack_id manquant.');
      if (!normalized.category_id) errors.push('category_id manquant.');
      if (!normalized.language_code) errors.push('language_code manquant.');
      if (!normalized.title_internal) errors.push('title_internal manquant.');
      if (!normalized.content_text) errors.push('Texte de l’énigme manquant.');
      if (!normalized.answer_display) errors.push('Réponse principale manquante.');

      if (normalized.language_code && !languageCodes.has(normalized.language_code)) {
        errors.push(`Langue inconnue : ${normalized.language_code}.`);
      }
      if (normalized.category_id && !categoryIds.has(normalized.category_id)) {
        errors.push(`Catégorie inconnue : ${normalized.category_id}.`);
      }
      if (normalized.content_pack_id && !packsById.has(normalized.content_pack_id)) {
        errors.push(`Pack inconnu : ${normalized.content_pack_id}.`);
      }

      const pack = packsById.get(normalized.content_pack_id);
      if (pack && pack.language_code !== normalized.language_code) {
        errors.push(`Le pack ${pack.id} est en ${pack.language_code}, pas en ${normalized.language_code}.`);
      }

      if (normalized.content_text.length > 0 && normalized.content_text.length < 80) {
        warnings.push('Texte très court (moins de 80 caractères).');
      }
      if (normalized.aliases.length === 0) {
        warnings.push('Aucun alias de réponse.');
      }
    }

    return {
      line: index + 1,
      raw,
      normalized,
      status: errors.length > 0 ? 'error' : warnings.length > 0 ? 'warning' : 'valid',
      errors,
      warnings,
    };
  });
}
